import { storableError } from '../../util/errors';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { ListingCategory, ListingType, TransactionProcessAlias, UnitType } from '../../util/enums';
import { showToaster } from '../../util/genericHelpers';
import moment from 'moment';
import { fetchAllListings } from '../DreamHomePage/DreamHomePage.duck';

// ================ Action types ================ //

export const CREATE_PROFILE_REQUEST = 'app/PropertyDeveloperPage/CREATE_PROFILE_REQUEST';
export const CREATE_PROFILE_SUCCESS = 'app/PropertyDeveloperPage/CREATE_PROFILE_SUCCESS';
export const CREATE_PROFILE_ERROR = 'app/PropertyDeveloperPage/CREATE_PROFILE_ERROR';

// ================ Reducer ================ //

const initialState = {
  createProfileInProgress: false,
  createProfileError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case CREATE_PROFILE_REQUEST:
      return { ...state, createProfileInProgress: true, createProfileError: null };
    case CREATE_PROFILE_SUCCESS:
      return { ...state, createProfileInProgress: false };
    case CREATE_PROFILE_ERROR:
      return { ...state, createProfileInProgress: false, createProfileError: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //
export const createProfileInProgressSelector = state => state.DreamHomePage.createProfileInProgress;

// ================ Action creators ================ //

export const createProfileRequest = () => ({ type: CREATE_PROFILE_REQUEST });

export const createProfileSuccess = () => ({ type: CREATE_PROFILE_SUCCESS });

export const createProfileError = e => ({
  type: CREATE_PROFILE_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

/**
 * Creates or updates a user profile and, if necessary, sends an admin notification.
 *
 * @param {object} params - The parameters for profile creation.
 * @param {object} params.completionDate - The completion date data.
 * @param {object} params.onsiteStartDate - The onsite start date data.
 * @param {object} params.plotAddress - The address data, with a potential selectedPlace.
 * @param {string} params.listingId - The unique identifier for the listing.
 * @param {boolean} params.isFinalStepCompleted - Flag indicating if the final step is completed.
 * @param {object} params.userFullName - The user's full name.
 * @param {object} rest - Any additional public data to be stored.
 * @returns {Promise<object>} The updated listing data.
 */
export const createProfile = params => async (dispatch, getState, sdk) => {
  dispatch(createProfileRequest());

  try {
    // Destructure params with default values to prevent undefined errors.
    const {
      completionDate = {},
      onsiteStartDate = {},
      plotAddress = {},
      listingId,
      isFinalStepCompleted,
      hasNoListing = false,
      userFullName = '',
      ...rest
    } = params;
    if (!listingId && !hasNoListing) {
      throw new Error('Listing ID is required for profile creation');
    }

    // Safely extract the address and origin from the provided plotAddress.
    const { address = '', origin = null } = plotAddress.selectedPlace || {};

    // Build publicData dynamically. Only include values if they are provided.
    const publicData = {
      ...rest,
      ...(address ? { plotAddress: address } : {}),
      ...(completionDate.date ? { completionDate: moment(completionDate.date).unix() } : {}),
      ...(onsiteStartDate.date ? { onsiteStartDate: moment(onsiteStartDate.date).unix() } : {}),
      transactionProcessAlias: TransactionProcessAlias.DEFAULT_INQUIRY,
      unitType: UnitType.INQUIRY,
      isFinalStepCompleted,
      listingType: ListingType.FREE_FLOW,
      listingCategory: ListingCategory.DREAM_HOME,
    };

    const basePayload = {
      ...(origin && { geolocation: origin }),
      publicData,
    };

    const listingPayload = hasNoListing
      ? { ...basePayload, title: userFullName.trim() }
      : { ...basePayload, id: listingId };

    const response = hasNoListing
      ? await sdk.ownListings.create(listingPayload, { expand: true })
      : await sdk.ownListings.update(listingPayload, { expand: true });
    const listing = response.data.data;

    // Validate that listing exists and has an id.
    if (!listing?.id) {
      throw new Error('Invalid response data: Listing not found');
    }
    // Dispatch success, show a success toaster and return the updated listing data.
    dispatch(createProfileSuccess());
    return listing;
  } catch (error) {
    // Handle errors by showing an error toaster, dispatching an error action, and rethrowing the error.
    const errorMessage = error.message || 'Failed to create profile';
    showToaster('error', errorMessage);
    dispatch(createProfileError(storableError(error)));
    throw error;
  }
};

export const loadData = (params, search, config) => async (dispatch, getState, sdk) => {
  const [user, listings] = await Promise.all([
    dispatch(fetchCurrentUser()),
    dispatch(fetchAllListings(config)),
  ]);
  return { user, listings };
};
